import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, ShoppingCart, Plus, Trash2 } from 'lucide-react';
import { useCreatePurchaseOrder, type POLineItemInput } from '../../../api/mutations/purchasing.mutations';
import { useSuppliers, type PurchaseRequest } from '../../../api/queries/purchasing.queries';
import { useBranches } from '../../../api/queries/settings.queries';

interface CreatePOModalProps {
  sourcePR: PurchaseRequest | null;
  onClose: () => void;
  onSuccess: () => void;
}

export default function CreatePOModal({ sourcePR, onClose, onSuccess }: CreatePOModalProps) {
  const { t } = useTranslation();

  const [supplierId, setSupplierId] = useState('');
  const [deliveryBranchId, setDeliveryBranchId] = useState(sourcePR?.branchId._id || '');
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
  const [taxRate, setTaxRate] = useState(7);
  const [shipping, setShipping] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<POLineItemInput[]>([]);

  const { data: suppliers } = useSuppliers({ isActive: true });
  const { data: branches } = useBranches();
  const createPO = useCreatePurchaseOrder();

  // Initialize items from source PR
  useEffect(() => {
    if (sourcePR) {
      setItems(
        sourcePR.items.map((item) => ({
          ingredientId: item.ingredientId?._id,
          customItemName: item.customItemName,
          isFreeFormItem: item.isFreeFormItem,
          quantity: item.quantity,
          unit: item.unit,
          unitPrice: item.estimatedPrice || 0,
          sourceItems: [
            {
              prId: sourcePR._id,
              prLineItemId: item._id,
              quantity: item.quantity,
              branchId: sourcePR.branchId._id,
            },
          ],
        }))
      );
    }
  }, [sourcePR]);

  const updateItem = (index: number, updates: Partial<POLineItemInput>) => {
    setItems(items.map((item, i) => (i === index ? { ...item, ...updates } : item)));
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const addItem = () => {
    setItems([
      ...items,
      {
        customItemName: '',
        isFreeFormItem: true,
        quantity: 1,
        unit: 'unit',
        unitPrice: 0,
      },
    ]);
  };

  const subtotal = items.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);
  const taxAmount = subtotal * (taxRate / 100);
  const total = subtotal + taxAmount + shipping - discount;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!supplierId) {
      alert(t('purchasing.selectSupplier'));
      return;
    }

    if (items.length === 0) {
      alert(t('purchasing.addAtLeastOneItem'));
      return;
    }

    try {
      await createPO.mutateAsync({
        prIds: sourcePR ? [sourcePR._id] : [],
        supplierId,
        deliveryBranchId,
        items,
        taxRate,
        shipping,
        discount,
        expectedDeliveryDate: expectedDeliveryDate || undefined,
        notes: notes || undefined,
      });
      onSuccess();
    } catch (error) {
      console.error('Failed to create PO:', error);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-lg bg-card flex flex-col">
        {/* Header */}
        <div className="border-b p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            <h2 className="text-xl font-bold">{t('purchasing.createPO')}</h2>
            {sourcePR && (
              <span className="text-sm text-muted-foreground">
                ({t('purchasing.fromPR')}: {sourcePR.prNumber})
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Supplier & Delivery */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('purchasing.supplier')} *</label>
                <select
                  value={supplierId}
                  onChange={(e) => setSupplierId(e.target.value)}
                  className="w-full rounded-lg border bg-background px-3 py-2"
                  required
                >
                  <option value="">{t('common.select')}</option>
                  {suppliers?.map((supplier) => (
                    <option key={supplier._id} value={supplier._id}>
                      {supplier.name}
                      {supplier.isPreferred && ` ⭐`}
                      {supplier.leadTimeDays && ` (${supplier.leadTimeDays} ${t('common.days')})`}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('purchasing.deliveryBranch')} *</label>
                <select
                  value={deliveryBranchId}
                  onChange={(e) => setDeliveryBranchId(e.target.value)}
                  className="w-full rounded-lg border bg-background px-3 py-2"
                  required
                >
                  <option value="">{t('common.select')}</option>
                  {branches?.map((branch) => (
                    <option key={branch._id} value={branch._id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">{t('purchasing.expectedDelivery')}</label>
              <input
                type="date"
                value={expectedDeliveryDate}
                onChange={(e) => setExpectedDeliveryDate(e.target.value)}
                className="w-full max-w-xs rounded-lg border bg-background px-3 py-2"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            {/* Items */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium">{t('purchasing.items')} *</label>
                <button
                  type="button"
                  onClick={addItem}
                  className="text-sm text-primary hover:underline flex items-center gap-1"
                >
                  <Plus className="h-3 w-3" />
                  {t('purchasing.addItem')}
                </button>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/50">
                    <tr>
                      <th className="text-left py-2 px-3">{t('purchasing.item')}</th>
                      <th className="text-right py-2 px-3 w-24">{t('purchasing.qty')}</th>
                      <th className="text-right py-2 px-3 w-20">{t('purchasing.unit')}</th>
                      <th className="text-right py-2 px-3 w-28">{t('purchasing.unitPrice')}</th>
                      <th className="text-right py-2 px-3 w-28">{t('purchasing.total')}</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={index} className="border-t">
                        <td className="py-2 px-3">
                          {item.isFreeFormItem ? (
                            <input
                              type="text"
                              value={item.customItemName || ''}
                              onChange={(e) => updateItem(index, { customItemName: e.target.value })}
                              placeholder={t('purchasing.itemName')}
                              className="w-full rounded border bg-background px-2 py-1"
                              required
                            />
                          ) : (
                            <span>
                              {sourcePR?.items.find((i) => i.ingredientId?._id === item.ingredientId)?.ingredientId?.name || item.ingredientId}
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-3">
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateItem(index, { quantity: parseFloat(e.target.value) || 0 })}
                            className="w-full rounded border bg-background px-2 py-1 text-right"
                            min={0.1}
                            step={0.1}
                            required
                          />
                        </td>
                        <td className="py-2 px-3">
                          <input
                            type="text"
                            value={item.unit}
                            onChange={(e) => updateItem(index, { unit: e.target.value })}
                            className="w-full rounded border bg-background px-2 py-1"
                            required
                          />
                        </td>
                        <td className="py-2 px-3">
                          <input
                            type="number"
                            value={item.unitPrice}
                            onChange={(e) => updateItem(index, { unitPrice: parseFloat(e.target.value) || 0 })}
                            className="w-full rounded border bg-background px-2 py-1 text-right"
                            min={0}
                            step={0.01}
                            required
                          />
                        </td>
                        <td className="py-2 px-3 text-right font-medium">
                          {formatCurrency(item.unitPrice * item.quantity)}
                        </td>
                        <td className="py-2 px-3">
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="p-1 text-destructive hover:bg-destructive/10 rounded"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {items.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <p className="text-sm">{t('purchasing.noItemsAdded')}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Totals */}
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('purchasing.taxRate')} (%)</label>
                    <input
                      type="number"
                      value={taxRate}
                      onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                      className="w-full rounded-lg border bg-background px-3 py-2"
                      min={0}
                      max={100}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('purchasing.shipping')}</label>
                    <input
                      type="number"
                      value={shipping}
                      onChange={(e) => setShipping(parseFloat(e.target.value) || 0)}
                      className="w-full rounded-lg border bg-background px-3 py-2"
                      min={0}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t('purchasing.discount')}</label>
                  <input
                    type="number"
                    value={discount}
                    onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                    className="w-full rounded-lg border bg-background px-3 py-2"
                    min={0}
                  />
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('purchasing.subtotal')}:</span>
                  <span>{formatCurrency(subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('purchasing.tax')} ({taxRate}%):</span>
                  <span>{formatCurrency(taxAmount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('purchasing.shipping')}:</span>
                  <span>{formatCurrency(shipping)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>{t('purchasing.discount')}:</span>
                    <span>-{formatCurrency(discount)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>{t('purchasing.total')}:</span>
                  <span>{formatCurrency(total)}</span>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium mb-1">{t('common.notes')}</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full rounded-lg border bg-background px-3 py-2"
                rows={3}
              />
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="border-t p-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border hover:bg-secondary transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={createPO.isPending || !supplierId || items.length === 0}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {createPO.isPending ? t('common.creating') : t('purchasing.createPO')}
          </button>
        </div>
      </div>
    </div>
  );
}
